import React, { useState } from "react";
import { AiLanguageVoice } from "../entities/ai-language-voice.entity";
import { FaGlobe, FaMicrophone, FaLink, FaInfoCircle } from "react-icons/fa";
import { RxCross2 } from "react-icons/rx";
import { MenuActions } from "src/components/menu-actions";
import {
  useDeleteAILanguageVoiceMutation,
  useUpdateAILanguageVoiceMutation,
} from "../ai-language-voices.service";

type VoiceEnum =
  | "FEMALE_CONFIDENT"
  | "FEMALE_FRIENDLY"
  | "MALE_CONFIDENT"
  | "MALE_FRIENDLY"
  | "MALE_STRICT";

type VoiceRow = AiLanguageVoice & { id: string };

type Props = { data: VoiceRow[] };

const sourceClass = (source: AiLanguageVoice["voiceSource"]) => {
  switch (source) {
    case "PREDEFINED":
      return "bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-800 border-emerald-200";
    case "INHERITED":
      return "bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border-amber-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

const voiceTypeColors: Record<string, string> = {
  FEMALE_CONFIDENT: "from-pink-100 to-rose-100 text-pink-800 border-pink-200",
  FEMALE_FRIENDLY: "from-purple-100 to-fuchsia-100 text-purple-800 border-purple-200",
  MALE_CONFIDENT: "from-blue-100 to-indigo-100 text-blue-800 border-blue-200",
  MALE_FRIENDLY: "from-cyan-100 to-sky-100 text-cyan-800 border-cyan-200",
  MALE_STRICT: "from-slate-100 to-gray-100 text-slate-800 border-slate-200",
};

/* ───────────────────── Delete Confirmation Dialog ───────────────────── */

const DeleteConfirmationDialog: React.FC<{
  voice: VoiceRow;
  onClose: () => void;
}> = ({ voice, onClose }) => {
  const [deleteVoice, { isLoading }] = useDeleteAILanguageVoiceMutation();

  const handleConfirm = async () => {
    await deleteVoice({ id: voice.id });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Confirm Delete</h2>
          <button
            onClick={onClose}
            className="text-2xl text-gray-400 hover:text-gray-700 cursor-pointer"
          >
            <RxCross2 />
          </button>
        </div>

        <p className="text-gray-600 mb-2">
          Are you sure you want to delete the AI language voice for{" "}
          <span className="font-bold text-gray-900">{voice.language}</span>?
        </p>
        <p className="text-sm text-red-500 mb-6">This action cannot be undone.</p>

        <div className="flex justify-end gap-3 border-t pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors cursor-pointer ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isLoading ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ───────────────────── Edit Voice Dialog ───────────────────── */

const EditVoiceDialog: React.FC<{
  voice: VoiceRow;
  onClose: () => void;
}> = ({ voice, onClose }) => {
  const [updateVoice, { isLoading }] = useUpdateAILanguageVoiceMutation();

  // Build initial selection from the current availableVoices
  const initialSelections: Record<string, string> = {};
  voice.availableVoices?.forEach((v) => {
    initialSelections[v.category] = v.voiceId;
  });

  const [selections, setSelections] = useState<Record<string, string>>(initialSelections);

  const handleChange = (category: string, voiceId: string) => {
    setSelections((prev) => ({ ...prev, [category]: voiceId }));
  };

  const handleSave = async () => {
    // Build updated availableVoices from selections
    const updatedVoices = Object.entries(selections).map(([category, voiceId]) => ({
      category: category as VoiceEnum,
      voiceId,
    }));

    await updateVoice({
      id: voice.id,
      updates: { availableVoices: updatedVoices },
    });
    onClose();
  };

  // Get the categories that have voice options in availableVoicesIds
  const categoriesWithOptions = Object.entries(voice.availableVoicesIds || {}).filter(
    ([, ids]) => ids && ids.length > 0
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">
            Edit Voices — {voice.language}
          </h2>
          <button
            onClick={onClose}
            className="text-2xl text-gray-400 hover:text-gray-700 cursor-pointer"
          >
            <RxCross2 />
          </button>
        </div>

        {categoriesWithOptions.length === 0 ? (
          <p className="text-gray-500 italic text-sm py-4">
            No available voice options for this language.
          </p>
        ) : (
          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            {categoriesWithOptions.map(([category, voiceIds]) => (
              <div key={category} className="border border-gray-200 rounded-lg p-3">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border bg-gradient-to-r ${
                      voiceTypeColors[category] ||
                      "from-gray-100 to-gray-100 text-gray-700 border-gray-200"
                    }`}
                  >
                    <FaMicrophone className="text-[7px]" />
                    {category.replace(/_/g, " ").toLowerCase()}
                  </span>
                </label>
                <select
                  value={selections[category] || ""}
                  onChange={(e) => handleChange(category, e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">Select a voice ID</option>
                  {voiceIds.map((vid: string) => (
                    <option key={vid} value={vid}>
                      {vid}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-3 border-t pt-4 mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md transition-colors cursor-pointer ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ───────────────────── Main Table ───────────────────── */

export const AILanguageVoicesTable: React.FC<Props> = ({ data }) => {
  const [deleteTarget, setDeleteTarget] = useState<VoiceRow | null>(null);
  const [editTarget, setEditTarget] = useState<VoiceRow | null>(null);

  return (
    <div className="overflow-x-auto mb-6 -mx-2 sm:mx-0">
      <table className="min-w-[1200px] sm:min-w-full border border-gray-200 divide-y divide-gray-200 text-[11px] sm:text-xs">
        <thead className="bg-gradient-to-r from-teal-900 to-emerald-900 text-white text-center text-[10px] sm:text-xs">
          <tr>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Language</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Voice Source</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Available Voices</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Inherited From</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Notes</th>
            <th className="px-2 py-3 sm:px-4 sm:py-4 uppercase tracking-wider font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr>
              <td colSpan={6} className="text-center py-8 text-xl text-gray-500">
                <div className="flex flex-col items-center gap-3">
                  <FaGlobe className="text-4xl text-gray-300" />
                  <span>No AI Supported Languages Found</span>
                </div>
              </td>
            </tr>
          )}
          {data.map((voice) => (
            <tr
              key={voice.id}
              className="text-center odd:bg-white even:bg-gradient-to-r even:from-teal-50/30 even:to-emerald-50/30 hover:bg-teal-100/50 transition-colors duration-200"
            >
              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 rounded-lg font-bold text-[12px] sm:text-sm border border-blue-200 shadow-sm">
                  <FaGlobe className="text-blue-500" />
                  {voice.language}
                </span>
              </td>
              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-semibold text-[13px] sm:text-[11px] border ${sourceClass(voice.voiceSource)}`}>
                  {voice.voiceSource === "INHERITED" ? <FaLink className="text-amber-600" /> : <FaMicrophone className="text-emerald-600" />}
                  {voice.voiceSource}
                </span>
              </td>
              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                <div className="flex flex-wrap justify-center gap-1.5 max-w-xs mx-auto">
                  {voice.availableVoices?.map((voiceType, idx) => (
                    <span
                      key={idx}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] sm:text-[12px] font-medium border bg-gradient-to-r ${voiceTypeColors[voiceType.category] || "from-gray-100 to-gray-100 text-gray-700 border-gray-200"}`}
                    >
                      <FaMicrophone className="text-[7px]" />
                      {voiceType.category.replace(/_/g, " ").toLowerCase()}
                    </span>
                  )) || (
                    <span className="text-gray-400 italic text-[10px]">No voices</span>
                  )}
                </div>
              </td>
              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200">
                {voice.inheritedFrom ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 rounded-lg font-medium text-[10px] sm:text-[11px] border border-orange-200">
                    <FaLink className="text-orange-500" />
                    {voice.inheritedFrom}
                  </span>
                ) : (
                  <span className="text-gray-400 italic text-[10px]">—</span>
                )}
              </td>
              <td className="px-2 py-3 sm:px-4 sm:py-4 border-t border-gray-200 max-w-[200px]">
                {voice.notes ? (
                  <div className="flex items-start gap-1 text-left">
                    <FaInfoCircle className="text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-[10px] sm:text-[11px] text-gray-600 line-clamp-2" title={voice.notes}>
                      {voice.notes}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-400 italic text-[10px]">—</span>
                )}
              </td>
              <td className="px-2 py-3 border-t border-gray-200">
                <MenuActions actions={[{
                  type: "BUTTON",
                  label: "Edit",
                  onClick: () => setEditTarget(voice),
                  color: "primary"
                }, {
                  type: "BUTTON",
                  label: "Delete",
                  onClick: () => setDeleteTarget(voice),
                  color: "danger"
                }]} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <DeleteConfirmationDialog
          voice={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {/* Edit Voice Dialog */}
      {editTarget && (
        <EditVoiceDialog
          voice={editTarget}
          onClose={() => setEditTarget(null)}
        />
      )}
    </div>
  );
};

export default AILanguageVoicesTable;
